import PointsWaveAnimation from "@/components/lazo-ai-chat-components/PointsWaveAnimation";
import { Colors } from "@/enums/Colors";
import { Button, ButtonProps, SxProps, styled } from "@mui/material";

export const CommonButtonSecondarySx = {
  backgroundColor: `${Colors.BUTTON_SECONDARY_PURPLE}`,
  color: `${Colors.LAZO_PURPLE}`,
  ":hover": {
    backgroundColor: `${Colors.BUTTON_SECONDARY_HOVER_PURPLE}`,
  },
};

/**
 * The pill style of the client's primary action on a task detail page ("Submit for review"). Shared
 * so every primary action the client can take on a task reads as the same control — including the
 * ones that replace it, like the signing review gate's "Approve for signing" (LS-1182).
 *
 * Use with `purpleBorderVariant`.
 */
export const ClientTaskActionSx: SxProps = {
  borderRadius: "1.25rem",
  px: "2rem",
  border: `2px solid ${Colors.BUTTON_PURPLE} !important`,
  backgroundColor: `${Colors.BUTTON_SECONDARY_PURPLE} !important`,
  color: `${Colors.BUTTON_PURPLE} !important`,
  fontWeight: "700 !important",
  "&:hover": {
    backgroundColor: `${Colors.BUTTON_SECONDARY_HOVER_PURPLE} !important`,
  },
};

export const RedButtonSx: SxProps = {
  transition: "all 0.15s ease-in",
  backgroundColor: Colors.HEART_RED,
  borderRadius: "0.5rem",
  color: Colors.WHITE,
  ":hover": {
    backgroundColor: Colors.HEART_RED,
  },
};

interface StyledButtonProps {
  backgroundColor?: string;
  color?: string;
}

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== "purpleBorderVariant" && prop !== "defaultVariant" && prop !== "customVariant" && prop !== "loadingColor",
})<StyledButtonProps>(({ backgroundColor, color, theme }) => ({
  textTransform: "none",
  borderRadius: "0.25rem",
  backgroundColor: backgroundColor ?? Colors.BUTTON_PURPLE,
  color: color ?? Colors.WHITE,
  whiteSpace: "nowrap",
  ":hover": {
    backgroundColor: backgroundColor ?? Colors.BUTTON_HOVER_PURPLE,
  },
  "&.Mui-disabled": {
    opacity: "0.6",
    color: Colors.WHITE,
  },
  "&.purple-border-variant": {
    color: color ?? Colors.BUTTON_PURPLE,
    backgroundColor: backgroundColor ?? Colors.WHITE,
    border: `1px solid ${Colors.BUTTON_PURPLE}`,
    ":hover": {
      backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    },
  },
  "&.round": {
    "& .MuiSvgIcon-root": {
      filter: "brightness(0)",
    },
    borderRadius: "50%",
    width: "1.5rem",
    minWidth: "0px",
    height: "1.5rem",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    },
  },
  "&.light-purple-background": {
    backgroundColor: Colors.BUTTON_SECONDARY_PURPLE,
    color: Colors.BUTTON_PURPLE,
  },
  [theme.breakpoints.down("md")]: {
    whiteSpace: "normal",
    height: "auto",
    minHeight: "2.75rem",
  },
}));

export type CommonButtonProps = ButtonProps & {
  children?: JSX.Element | JSX.Element[] | React.ReactNode;
  purpleBorderVariant?: boolean;
  backgroundColor?: string;
  roundButton?: boolean;
  color?: string;
  customVariant?: "round" | "purple-border-variant" | "light-purple-background";
  loading?: boolean;
  loadingColor?: Colors;
};

function CommonButton(props: CommonButtonProps) {
  return (
    <StyledButton
      disabled={props.loading}
      className={props.customVariant ? props.customVariant : props.purpleBorderVariant ? "purple-border-variant" : "default-variant"}
      {...props}
    >
      {props.loading ? (
        <PointsWaveAnimation
          points={3}
          pointsColor={props.loadingColor || Colors.LAZO_PURPLE}
          containerProps={{
            sx: {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            },
          }}
        />
      ) : (
        props.children
      )}
    </StyledButton>
  );
}

export default CommonButton;
